import { useState } from "react";
import { Smartphone, X, Apple } from "lucide-react";
import { useShop } from "@/context/ShopContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function detectPlatform() {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent || navigator.vendor || "";
  if (/android/i.test(ua)) return "android";
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return "ios";
  return "other";
}

// Floating "Get the app" promo (web only). Store links + copy are owner-editable in Settings.
// Hidden entirely when disabled or when neither store link is set.
export default function AppDownloadPromo() {
  const { shop } = useShop();
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const android = (shop?.app_download_android_url || "").trim();
  const ios = (shop?.app_download_ios_url || "").trim();
  const enabled = shop?.app_download_enabled !== false;
  if (!enabled || (!android && !ios) || dismissed) return null;

  const platform = detectPlatform();
  const primaryHref = platform === "ios" ? ios || android : android || ios;

  const handleOpen = () => {
    // One store configured → go straight there; both → let the user choose.
    if (android && ios) setOpen(true);
    else if (primaryHref) window.open(primaryHref, "_blank", "noopener");
  };

  return (
    <>
      <div className="fixed bottom-20 right-4 z-30 flex items-center gap-1 md:bottom-6 md:right-6">
        <button
          onClick={handleOpen}
          data-testid="app-download-button"
          className="flex items-center gap-2 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground shadow-lg transition hover:opacity-90"
        >
          <Smartphone className="h-4 w-4" />
          {shop?.app_download_title || "Get the app"}
        </button>
        <button
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
          data-testid="app-download-dismiss"
          className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary text-muted-foreground shadow"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{shop?.app_download_title || "Get the Goldys app"}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <p className="text-sm text-muted-foreground">
              {shop?.app_download_subtitle || "Book faster, track orders and play games on the go."}
            </p>
            {android ? (
              <a
                href={android}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="app-download-android"
                className="flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground"
              >
                <Smartphone className="h-5 w-5" /> Download for Android
              </a>
            ) : null}
            {ios ? (
              <a
                href={ios}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="app-download-ios"
                className="flex items-center justify-center gap-2 rounded-xl border border-primary px-4 py-3 text-sm font-semibold text-primary"
              >
                <Apple className="h-5 w-5" /> Download for iOS
              </a>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
